import { useCallback, useEffect, useRef } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useCurrent } from '@/hooks/useCurrent';
import { useApplicablePromoCodes } from '@/imports/ui/components/Cart/hooks/useCartQueries';
import { cartActions, cartVar } from './cartState';

/**
 * Hook for managing cart promo codes
 * 
 * This hook handles fetching and managing applicable promo codes for the cart.
 * It should be used in components that need to display or interact with promo codes.
 * 
 * Usage:
 * ```tsx
 * const { availablePromoCodes, loading, refetch } = useCartPromoCodes();
 * ```
 */
export const useCartPromoCodes = (options?: { 
  skip?: boolean;
  autoFetch?: boolean;
}) => {
  const { user } = useCurrent();
  const cartState = useReactiveVar(cartVar);
  const { refetch: refetchPromoCodes } = useApplicablePromoCodes({
    skip: true,
  });
  
  const hasFetchedRef = useRef(false);
  const lastCartItemsRef = useRef<string>('');
  const skip = options?.skip ?? false;
  const autoFetch = options?.autoFetch ?? true;

  // Create a string representation of cart items including quantities
  // We need to refetch when quantities change because quantity-based promo codes
  // need to check if the user has enough of the same item type to meet minimum requirements
  const cartItemsKey = cartState.items
    .map(item => `${item._id}:${item.quantity}`)
    .sort()
    .join(',');

  // Fetch promo codes when cart has items and auto-fetch is enabled
  useEffect(() => {
    if (skip || !autoFetch || !user?._id || cartState.items.length === 0) {
      hasFetchedRef.current = false;
      lastCartItemsRef.current = '';
      return;
    }

    // Check if cart items have changed (added/removed or quantity changed)
    const cartItemsChanged = lastCartItemsRef.current !== cartItemsKey;
    
    // Fetch if we haven't fetched yet, or if cart items have changed
    if (!hasFetchedRef.current || cartItemsChanged) {
      hasFetchedRef.current = true;
      lastCartItemsRef.current = cartItemsKey;
      
      refetchPromoCodes()
        .then(({ data }) => {
          if (data?.getApplicablePromoCodes?.promoCodes) {
            cartActions.setAvailablePromoCodes(
              data.getApplicablePromoCodes.promoCodes
            );
          }
        })
        .catch(console.error);
    }
  }, [skip, autoFetch, user?._id, cartState.items.length, cartItemsKey, refetchPromoCodes]);

  // Reset fetch flag when cart is empty
  useEffect(() => {
    if (cartState.items.length === 0) {
      hasFetchedRef.current = false;
      lastCartItemsRef.current = '';
    }
  }, [cartState.items.length]);

  // Manual refetch function
  const refetch = useCallback(async () => {
    if (!user?._id) return;
    
    try {
      cartActions.setPromoCodeLoading(true);
      const { data } = await refetchPromoCodes();
      if (data?.getApplicablePromoCodes?.promoCodes) {
        cartActions.setAvailablePromoCodes(
          data.getApplicablePromoCodes.promoCodes
        );
      }
      hasFetchedRef.current = true;
    } catch (error) {
      console.error('Failed to refetch promo codes:', error);
    } finally {
      cartActions.setPromoCodeLoading(false);
    }
  }, [refetchPromoCodes, user?._id]);

  return {
    availablePromoCodes: cartState.availablePromoCodes,
    loading: cartState.promoCodeLoading,
    refetch,
  };
};

